import { APP_PATHS } from 'app-shared/constants';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { HomeRoute } from '@/app/components/HomeRoute';
import { AppLayout } from '@/app/components/AppLayout';
import { AuthGate } from '@/auth/components/AuthGate';
import { AUTH_MODE } from '@/auth/constants/AuthMode';
import { SessionGate } from '@/session/components/SessionGate';
import { SETTINGS_ROOT_PATH } from '@/settings/constants/SettingsRootPath';
import { CLERK_MEMBERS_PAGE_PATH } from '@/settings/members/constants/ClerkMembersPagePath';
import { FullScreenLoadingState } from '@/ui/feedback/loading-state/components/FullScreenLoadingState';

const SignInPage = lazy(() =>
  import('~/pages/auth/SignInPage').then((module) => ({
    default: module.SignInPage,
  })),
);

const SignUpPage = lazy(() =>
  import('~/pages/auth/SignUpPage').then((module) => ({
    default: module.SignUpPage,
  })),
);

const OnboardingWorkspacePage = lazy(() =>
  import('~/pages/auth/OnboardingWorkspacePage').then((module) => ({
    default: module.OnboardingWorkspacePage,
  })),
);

const MyPlanningPage = lazy(() =>
  import('~/pages/my-planning/MyPlanningPage').then((module) => ({
    default: module.MyPlanningPage,
  })),
);

const PlanningPage = lazy(() =>
  import('~/pages/planning/PlanningPage').then((module) => ({
    default: module.PlanningPage,
  })),
);

const BacklogPage = lazy(() =>
  import('~/pages/backlog/BacklogPage').then((module) => ({
    default: module.BacklogPage,
  })),
);

const AbsencesPage = lazy(() =>
  import('~/pages/absences/AbsencesPage').then((module) => ({
    default: module.AbsencesPage,
  })),
);

const SettingsProfilePage = lazy(() =>
  import('~/pages/settings/SettingsProfilePage').then((module) => ({
    default: module.SettingsProfilePage,
  })),
);

const SettingsSecurityPage = lazy(() =>
  import('~/pages/settings/SettingsSecurityPage').then((module) => ({
    default: module.SettingsSecurityPage,
  })),
);

const SettingsAppearancePage = lazy(() =>
  import('~/pages/settings/SettingsAppearancePage').then((module) => ({
    default: module.SettingsAppearancePage,
  })),
);

const SettingsWorkspacePage = lazy(() =>
  import('~/pages/settings/SettingsWorkspacePage').then((module) => ({
    default: module.SettingsWorkspacePage,
  })),
);

const SettingsPlanningPage = lazy(() =>
  import('~/pages/settings/SettingsPlanningPage').then((module) => ({
    default: module.SettingsPlanningPage,
  })),
);

const SettingsTeamPage = lazy(() =>
  import('~/pages/settings/SettingsTeamPage').then((module) => ({
    default: module.SettingsTeamPage,
  })),
);
const SettingsInterventionTypesPage = lazy(() =>
  import('~/pages/settings/SettingsInterventionTypesPage').then((module) => ({
    default: module.SettingsInterventionTypesPage,
  })),
);
const SettingsTeamsAndSkillsPage = lazy(() =>
  import('~/pages/settings/SettingsTeamsAndSkillsPage').then((module) => ({
    default: module.SettingsTeamsAndSkillsPage,
  })),
);

const SettingsMembersPage = lazy(() =>
  import('~/pages/settings/SettingsMembersPage').then((module) => ({
    default: module.SettingsMembersPage,
  })),
);

const SettingsBillingPage = lazy(() =>
  import('~/pages/settings/SettingsBillingPage').then((module) => ({
    default: module.SettingsBillingPage,
  })),
);

const NotFoundPage = lazy(() =>
  import('~/pages/not-found/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  })),
);

// Sign-in pages first (Clerk only: the mock signs everyone in, so its sign-in
// links open the app). Then AuthGate (with Clerk: signed in, with a
// workspace) and SessionGate: no page shows before the signed-in user and
// their workspace are loaded. Profile and members end with /* because Clerk's
// account and member screens have pages of their own.
export const AppRouter = () => (
  <Suspense fallback={<FullScreenLoadingState />}>
    <Routes>
      {AUTH_MODE === 'clerk' ? (
        <>
          <Route path={`${APP_PATHS.signIn}/*`} element={<SignInPage />} />
          <Route path={`${APP_PATHS.signUp}/*`} element={<SignUpPage />} />
          <Route
            path={APP_PATHS.onboardingWorkspace}
            element={<OnboardingWorkspacePage />}
          />
        </>
      ) : (
        <>
          <Route
            path={`${APP_PATHS.signIn}/*`}
            element={<Navigate to={APP_PATHS.home} replace />}
          />
          <Route
            path={`${APP_PATHS.signUp}/*`}
            element={<Navigate to={APP_PATHS.home} replace />}
          />
        </>
      )}
      <Route element={<AuthGate />}>
        <Route element={<SessionGate />}>
          <Route element={<AppLayout />}>
            <Route path={APP_PATHS.home} element={<HomeRoute />} />
            <Route path={APP_PATHS.myPlanning} element={<MyPlanningPage />} />
            <Route path={APP_PATHS.planning} element={<PlanningPage />} />
            <Route path={APP_PATHS.backlog} element={<BacklogPage />} />
            <Route path={APP_PATHS.absences} element={<AbsencesPage />} />
            <Route
              path={SETTINGS_ROOT_PATH}
              element={<Navigate to={APP_PATHS.settingsProfile} replace />}
            />
            <Route
              path={`${APP_PATHS.settingsProfile}/*`}
              element={<SettingsProfilePage />}
            />
            {AUTH_MODE === 'clerk' && (
              <Route
                path={`${APP_PATHS.settingsSecurity}/*`}
                element={<SettingsSecurityPage />}
              />
            )}
            <Route
              path={APP_PATHS.settingsAppearance}
              element={<SettingsAppearancePage />}
            />
            <Route
              path={APP_PATHS.settingsPlanning}
              element={<SettingsPlanningPage />}
            />
            <Route
              path={APP_PATHS.settingsTeam}
              element={<SettingsTeamPage />}
            />
            <Route
              path={APP_PATHS.settingsInterventionTypes}
              element={<SettingsInterventionTypesPage />}
            />
            <Route
              path={APP_PATHS.settingsTeamsAndSkills}
              element={<SettingsTeamsAndSkillsPage />}
            />
            <Route
              path={APP_PATHS.settingsWorkspace}
              element={<SettingsWorkspacePage />}
            />
            {AUTH_MODE === 'clerk' && (
              <Route
                path={APP_PATHS.settingsMembers}
                element={<Navigate to={CLERK_MEMBERS_PAGE_PATH} replace />}
              />
            )}
            <Route
              path={`${APP_PATHS.settingsMembers}/*`}
              element={<SettingsMembersPage />}
            />
            <Route
              path={APP_PATHS.settingsBilling}
              element={<SettingsBillingPage />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  </Suspense>
);
